import React, { useState, useEffect } from "react";
import Select from "react-select";
import { OrderSelectStyles } from "../../constants/index.js";
import { getTranslation, getLocalizedValue } from "../../utils/translate.js";
import { getTranslatedPaymentType, getPaymentIconUrl, getPaymentIconClassName } from "../../utils/paymentUtils.js";
import chevronRight from "../../assets/chevron-right.svg";
import { useDarkMode } from "../../utils/darkModeContext.js";
import { useLanguage } from "../../utils/languageContext.js";
import { formatDate, formatTime } from "../../utils/dateUtils.js";
import { DISH_IMAGE_PLACEHOLDER_40 } from "../../utils/dishImagePlaceholder.js";
import { resolveOrderCustomer } from "../../utils/orderCustomer.js";

const getOrderDishAdditions = (dish) =>
    Array.isArray(dish?.orderDishAdditionDtoList) ? dish.orderDishAdditionDtoList : [];

/**
 * Kitchen-facing label: group + addition name, localized.
 * @param {Object} item - orderDishAdditionDtoList entry
 * @param {string} language
 * @returns {string}
 */
const getAdditionLabel = (item, language) => {
    const groupName = getLocalizedValue(item?.dishAdditionDto?.group, "name", language);
    const additionName = getLocalizedValue(item?.dishAdditionDto, "name", language);
    if (groupName && additionName) {
        return `${groupName}: ${additionName}`;
    }
    return additionName || groupName || "";
};

/**
 * OrderCard component - Displays a single order
 * @param {Object} props - Component props
 * @param {Object} props.order - Order data
 * @param {string} props.activeTab - Active tab ('ongoing' or 'history')
 * @param {Array} props.selectOptions - Translated select options
 * @param {Object} props.selectedOptions - Selected options state
 * @param {Function} props.onStatusChange - Handler for status change
 * @param {Function} props.onViewOrder - Handler for viewing full order
 * @param {Function} props.getOptionStyles - Function to get option styles
 * @returns {JSX.Element} Order card component
 */
const OrderCard = ({
    order,
    activeTab,
    selectOptions,
    selectedOptions,
    onStatusChange,
    onViewOrder,
    getOptionStyles
}) => {
    const { language } = useLanguage();
    const { isDarkMode } = useDarkMode();
    const [isMobile, setIsMobile] = useState(false);

    // Detect mobile screen size
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 694);
        };
        
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Guard against undefined order
    if (!order || !order.status) {
        return null;
    }

    const orderStatus = order.status || "REQUESTED"; // Default status if missing
    const isHistoryStatus = ["DELIVERED", "DECLINED", "INITIAL"].includes(orderStatus);
    const canChangeStatus = activeTab === "ongoing" && !isHistoryStatus;
    // Ensure currentOption always has a valid value
    const savedOption = selectedOptions[order?.id];
    const currentOption = (savedOption && savedOption.value) ? savedOption : { value: orderStatus };
    const isTakeaway = order?.deliveryMethod === 'TAKEAWAY';
    // For non-BYCHEF orders the customer/company come from the externalUser snapshot.
    const customer = resolveOrderCustomer(order);
    const customerFullName = customer.fullName;
    const displayCompanyName = customer.companyName;

    return (
        <div className="mt-4">
            <div className="border-t custom-sm:border-t border-0 pt-4 bg-white dark:bg-[#161b22] rounded-lg shadow-sm dark:shadow-md dark:border-[#30363d] p-4 border-gray-200 dark:border-[#30363d]">
                {/* Order header with status indicator */}
                <div className="flex justify-between items-start mb-3">
                    {/* Desktop view selector */}
                    <div className="hidden sm:flex items-center">
                        {!canChangeStatus ? (
                            <div
                                className="px-4 py-2 rounded-md font-bold text-sm whitespace-nowrap"
                                style={{
                                    backgroundColor: getOptionStyles({ value: orderStatus }).backgroundColor,
                                    color: getOptionStyles({ value: orderStatus }).color
                                }}
                            >
                                {getTranslation(`orderStatuses.${orderStatus.toLowerCase()}`, language)}
                            </div>
                        ) : (
                            <Select
                                className="w-[221px] whitespace-nowrap"
                                options={selectOptions}
                                placeholder={getTranslation(`orderStatuses.${orderStatus.toLowerCase()}`, language)}
                                styles={{
                                    ...OrderSelectStyles,
                                    control: (provided, state) => ({
                                        ...provided,
                                        paddingRight: "8px", // Consistent padding to move arrow away from right edge
                                        backgroundColor: getOptionStyles(currentOption).backgroundColor,
                                        color: getOptionStyles(currentOption).color,
                                        borderColor: getOptionStyles(currentOption).color,
                                        boxShadow: state.isFocused
                                            ? `0 0 0 1px ${getOptionStyles(currentOption).color}`
                                            : null,
                                        "&:hover": {
                                            borderColor: getOptionStyles(currentOption).color
                                        },
                                        fontWeight: "700",
                                        fontSize: "14px",
                                        caretColor: "transparent"
                                    }),
                                    singleValue: (provided) => ({
                                        ...provided,
                                        color: getOptionStyles(currentOption).color
                                    }),
                                    option: (provided, { data, isSelected }) => ({
                                        ...provided,
                                        backgroundColor: isSelected
                                            ? getOptionStyles(data).backgroundColor
                                            : isDarkMode
                                            ? "#161b22"
                                            : "white",
                                        color: getOptionStyles(data).color,
                                        fontWeight: "700",
                                        fontSize: "14px",
                                        ":hover": {
                                            backgroundColor: getOptionStyles(data).backgroundColor,
                                            opacity: 0.8
                                        }
                                    }),
                                    dropdownIndicator: (provided) => ({
                                        ...provided,
                                        color: getOptionStyles(currentOption).color,
                                        "&:hover": {
                                            color: getOptionStyles(currentOption).color
                                        }
                                    })
                                }}
                                value={selectOptions.find((option) => option.value === (currentOption?.value || orderStatus)) || null}
                                onChange={(option) => onStatusChange(order?.id, option)}
                            />
                        )}
                        <button
                            className="flex text-gray-900 dark:text-[#c9d1d9] items-center font-bold ml-3 text-sm hover:text-gray-700 dark:hover:text-[#b1bac4] transition-colors"
                            onClick={() => onViewOrder(order)}
                        >
                            {getTranslation("orderPage.viewFullOrder", language)}
                            <img
                                className="w-4 h-4 ml-1 dark:invert dark:opacity-80"
                                src={chevronRight || ""}
                                alt={getTranslation("pagination.next", language)}
                            />
                        </button>
                    </div>
                </div>

                {/* Order info details */}
                <div className="bg-white dark:bg-[#0d1117] rounded-lg p-3 mb-3 border border-gray-100 dark:border-[#30363d]">
                    <div className="text-sm text-gray-600 dark:text-[#b1bac4]">
                        <div className="flex items-start mb-2">
                            <span className="font-semibold mr-1 min-w-[110px]">
                                {getTranslation("orderPage.orderNumber", language)}:
                            </span>
                            <span className="text-gray-700 dark:text-[#c9d1d9] font-medium">{order?.number}</span>
                        </div>
                        {displayCompanyName && (
                            <div className="flex items-start mb-2">
                                <span className="font-semibold mr-1 min-w-[110px] dark:text-[#b1bac4]">
                                    {getTranslation("orderPage.company", language) || "Company"}:
                                </span>
                                <span className="text-gray-700 dark:text-[#c9d1d9] font-medium break-words">{displayCompanyName}</span>
                            </div>
                        )}
                        {customerFullName && (
                            <div className="flex items-start mb-2">
                                <span className="font-semibold mr-1 min-w-[110px] dark:text-[#b1bac4]">
                                    {getTranslation("orderPage.customer", language) || "Customer"}:
                                </span>
                                <span className="text-gray-700 dark:text-[#c9d1d9] font-medium break-words">{customerFullName}</span>
                            </div>
                        )}
                        <div className="flex items-start mb-2">
                            <span className="font-semibold mr-1 min-w-[110px] dark:text-[#b1bac4]">
                                {getTranslation("orderContentPage.scheduledDate", language) || getTranslation("orderPage.orderDeliveryDate", language)}:
                            </span>
                            <span className="text-gray-700 dark:text-[#c9d1d9] font-medium">
                                {formatDate(order?.deliveryDateTime)}
                            </span>
                        </div>
                        {order?.deliveryDateTime && (
                            <div className="flex items-start mb-2">
                                <span className="font-semibold mr-1 min-w-[110px] dark:text-[#b1bac4]">
                                    {getTranslation("orderContentPage.scheduledTime", language)}:
                                </span>
                                <div className="flex flex-col sm:flex-row sm:items-center gap-2 flex-1 min-w-0">
                                    <span className="text-gray-700 dark:text-[#c9d1d9] font-medium whitespace-nowrap">
                                        {formatTime(order?.deliveryDateTime)}
                                    </span>
                                    {order?.asSoonAsPossible && (
                                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-white dark:bg-gray-800/50 text-gray-800 dark:text-gray-400 border border-gray-200 dark:border-gray-700 flex-shrink-0 w-fit">
                                            <svg className="w-3 h-3 mr-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                                            </svg>
                                            <span className="whitespace-nowrap">
                                                {isMobile 
                                                    ? getTranslation('orderContentPage.asapShort', language)
                                                    : getTranslation('orderContentPage.asSoonAsPossible', language)
                                                }
                                            </span>
                                        </span>
                                    )}
                                </div>
                            </div>
                        )}
                        <div className="flex items-start mb-2">
                            <span className="font-semibold mr-1 min-w-[110px] dark:text-[#b1bac4]">
                                {getTranslation("orderContentPage.deliveryWay", language) || "Delivery Method"}:
                            </span>
                            <div className="flex items-center">
                                {isTakeaway ? (
                                    <div className="flex items-center justify-center w-5 h-5 rounded-full bg-gray-200 dark:bg-gray-700 mr-2">
                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4 text-gray-700 dark:text-gray-300">
                                            <path d="M8 7a4 4 0 1 1 8 0v1h1.5A1.5 1.5 0 0 1 19 9.5v9A2.5 2.5 0 0 1 16.5 21h-9A2.5 2.5 0 0 1 5 18.5v-9A1.5 1.5 0 0 1 6.5 8H8V7Zm2 1h4V7a2 2 0 1 0-4 0v1Z"/>
                                        </svg>
                                    </div>
                                ) : (
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" className="w-5 h-5 mr-2 text-gray-700 dark:text-gray-400" strokeWidth={2.5}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 7h12v7H3zM15 10h3l3 3v1h-6V10zM6 20a2 2 0 1 1 0-4 2 2 0 0 1 0 4Zm10 0a2 2 0 1 1 0-4 2 2 0 0 1 0 4Z" />
                                    </svg>
                                )}
                                <span className="text-gray-700 dark:text-[#c9d1d9] font-medium">
                                    {isTakeaway ? getTranslation("orderContentPage.takeaway", language) : getTranslation("orderContentPage.delivery", language)}
                                </span>
                            </div>
                        </div>
                        {!isTakeaway && (
                            <div className="flex items-start mb-2">
                                <span className="font-semibold mr-1 min-w-[110px] dark:text-[#b1bac4]">
                                    {getTranslation("orderPage.shippingAddress", language)}:
                                </span>
                                <span className="text-gray-700 dark:text-[#c9d1d9] font-medium">
                                    {order?.addressDto?.city} {order?.addressDto?.country} {order?.addressDto?.street}{" "}
                                    {order?.addressDto?.home}
                                </span>
                            </div>
                        )}
                        <div className="flex items-center">
                            <span className="font-semibold mr-1 min-w-[110px] dark:text-[#b1bac4]">
                                {getTranslation("orderPage.payment", language) || "Payment"}:
                            </span>
                            <div className="flex items-center flex-wrap gap-2">
                                <div className="flex items-center">
                                    {order?.paymentType === "TELCELL" ? (
                                        <svg
                                            className={getPaymentIconClassName(order?.paymentType)}
                                            fill="none"
                                            viewBox="0 0 162 30"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                d="M16.3593 1.05288V28.9605H9.18531V8.17342H0.733398V1.05288H16.3593ZM18.6001 8.17954H25.7741V1.05899H18.6001V8.17954ZM47.4804 28.9605V21.8461H35.2543V18.5701H44.6096V11.4495H35.2543V8.17342H47.4804V1.05288H28.0802V28.9605H47.4804ZM69.3948 28.9605V21.8461H58.0543V1.05288H50.8802V28.9605H69.3948ZM83.4516 8.14897C86.2273 8.14897 88.1411 9.85424 88.8544 11.8406L96.4862 11.8468C95.6774 5.61247 90.2274 0.600586 83.3446 0.600586C75.2077 0.600586 69.5434 6.62707 69.5434 15.0006C69.5434 23.3741 75.3979 29.4006 83.3446 29.4006C90.061 29.4006 95.446 25.006 96.5748 18.1789L88.8247 18.1727C88.1352 20.3608 86.0074 21.8522 83.4456 21.8522C79.6476 21.8522 77.0681 19.0285 77.0681 15.0006C77.0741 10.9727 79.5585 8.14897 83.4516 8.14897ZM98.6374 1.05288V28.9605H118.038V21.8461H105.811V18.5701H115.167V11.4495H105.811V8.17342H118.038V1.05288H98.6374ZM139.941 28.9605V21.8461H128.605V1.05288H121.431V28.9605H139.941ZM149.854 1.05288H142.681V28.9605H161.189V21.8461H149.854V1.05288Z"
                                                className="fill-gray-900 dark:fill-white"
                                            />
                                        </svg>
                                    ) : (order?.paymentType === "CASH" || order?.paymentType === "B2B") ? (
                                        <svg
                                            className={getPaymentIconClassName(order?.paymentType)}
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                            strokeWidth={2}
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"
                                                className="stroke-gray-600 dark:stroke-gray-400"
                                            />
                                        </svg>
                                    ) : getPaymentIconUrl(order?.paymentType) ? (
                                        <img
                                            className={getPaymentIconClassName(order?.paymentType)}
                                            src={getPaymentIconUrl(order?.paymentType)}
                                            alt={order?.paymentType || ""}
                                        />
                                    ) : null}
                                    <span className={`${order?.paymentType !== "CASH" && order?.paymentType !== "B2B" && getPaymentIconUrl(order?.paymentType) ? "ml-2" : ""} text-gray-700 dark:text-[#c9d1d9] font-medium`}>
                                        {getTranslatedPaymentType(order?.paymentType, language)}
                                    </span>
                                </div>
                                
                                {order?.orderPaymentList?.length > 0 && (
                                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${
                                        order.orderPaymentList[0].status === 'PAYED' 
                                            ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300 border border-green-200 dark:border-green-800/50' 
                                            : 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 border border-amber-200 dark:border-amber-800/50'
                                    }`}>
                                        {getTranslation(`paymentStatuses.${order.orderPaymentList[0].status?.toLowerCase()}`, language) || order.orderPaymentList[0].status}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Order items */}
                <div>
                    <div className="flex flex-wrap items-stretch gap-2">
                        {(order?.orderDishList ?? []).map((dish, index) => {
                            const additions = getOrderDishAdditions(dish)
                                .map((item, idx) => ({
                                    key: item?.id ?? item?.dishAdditionDto?.id ?? idx,
                                    label: getAdditionLabel(item, language)
                                }))
                                .filter((item) => item.label);
                            const hasAdditions = additions.length > 0;

                            return (
                                <div
                                    key={index}
                                    className={`flex items-start gap-3 bg-white dark:bg-[#0d1117] rounded-xl border p-3 w-full min-w-0 sm:w-max sm:min-w-[12rem] sm:max-w-md ${
                                        hasAdditions
                                            ? "border-orange-200 dark:border-orange-900/50"
                                            : "border-gray-100 dark:border-[#30363d]"
                                    }`}
                                >
                                    <img
                                        src={dish?.dishDto?.url || DISH_IMAGE_PLACEHOLDER_40}
                                        alt={getTranslation("common.productThumbnail", language)}
                                        className="w-[3.75rem] h-[3.75rem] shrink-0 rounded-lg object-cover"
                                        onError={(e) => {
                                            e.target.onerror = null;
                                            e.target.src = DISH_IMAGE_PLACEHOLDER_40;
                                        }}
                                    />
                                    <div className="min-w-0 flex-1">
                                        <span className="text-xs sm:text-sm font-medium block text-gray-900 dark:text-[#c9d1d9] leading-snug break-words">
                                            {getLocalizedValue(dish?.dishDto, "name", language) || "-"}
                                        </span>
                                        <span className="text-xs text-gray-500 dark:text-[#8b949e] font-medium mt-0.5 block">
                                            x {dish?.quantity}
                                        </span>
                                        {hasAdditions && (
                                            <ul
                                                className="mt-1.5 flex flex-wrap gap-1"
                                                aria-label={getTranslation("orderContentPage.additions", language) || "Additions"}
                                            >
                                                {additions.map((item) => (
                                                    <li
                                                        key={item.key}
                                                        title={item.label}
                                                        className="inline-flex max-w-full items-center rounded-md border border-orange-100 bg-orange-50 px-1.5 py-0.5 text-[11px] font-medium leading-tight text-orange-800 dark:border-orange-900/40 dark:bg-orange-950/35 dark:text-orange-200/90"
                                                    >
                                                        <span className="mr-0.5 shrink-0 opacity-70" aria-hidden>+</span>
                                                        <span className="min-w-0 break-words">{item.label}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Mobile view order action buttons */}
                <div className="mt-4 block sm:hidden">
                    {canChangeStatus ? (
                        <div className="flex flex-col space-y-3">
                            <div className="relative">
                                <Select
                                    className="w-full shadow-sm"
                                    options={selectOptions}
                                    placeholder={getTranslation(`orderStatuses.${orderStatus.toLowerCase()}`, language)}
                                    styles={{
                                        ...OrderSelectStyles,
                                        control: (provided, state) => ({
                                            ...provided,
                                            backgroundColor: getOptionStyles(currentOption).backgroundColor,
                                            color: getOptionStyles(currentOption).color,
                                            borderColor: "transparent",
                                            boxShadow: state.isFocused
                                                ? `0 0 0 2px ${getOptionStyles(currentOption).color}`
                                                : "0 1px 3px rgba(0,0,0,0.1)",
                                            "&:hover": {
                                                borderColor: "transparent"
                                            },
                                            borderRadius: "8px",
                                            fontWeight: "700",
                                            fontSize: "14px",
                                            caretColor: "transparent",
                                            height: "44px",
                                            minHeight: "44px"
                                        }),
                                        singleValue: (provided) => ({
                                            ...provided,
                                            color: getOptionStyles(currentOption).color
                                        }),
                                        option: (provided, { data, isSelected }) => ({
                                            ...provided,
                                            backgroundColor: isSelected
                                                ? getOptionStyles(data).backgroundColor
                                                : isDarkMode
                                                ? "#161b22"
                                                : "white",
                                            color: getOptionStyles(data).color,
                                            fontWeight: "700",
                                            fontSize: "14px",
                                            padding: "10px 14px",
                                            ":hover": {
                                                backgroundColor: getOptionStyles(data).backgroundColor,
                                                opacity: 0.8
                                            }
                                        }),
                                        dropdownIndicator: (provided) => ({
                                            ...provided,
                                            color: getOptionStyles(currentOption).color,
                                            "&:hover": {
                                                color: getOptionStyles(currentOption).color
                                            }
                                        }),
                                        menu: (provided) => ({
                                            ...provided,
                                            zIndex: 20,
                                            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                                            border: "none",
                                            borderRadius: "8px"
                                        }),
                                        menuList: (provided) => ({
                                            ...provided,
                                            padding: "6px",
                                            borderRadius: "8px"
                                        })
                                    }}
                                    value={selectOptions.find((option) => option.value === (currentOption?.value || orderStatus)) || null}
                                    onChange={(option) => onStatusChange(order?.id, option)}
                                />
                            </div>
                            <button
                                className="w-full py-3 px-4 rounded-lg text-sm font-bold flex justify-center items-center bg-customBlack dark:bg-[#21262d] text-white dark:text-[#c9d1d9] hover:bg-customHoverBlack dark:hover:bg-[#30363d] transition-colors shadow-sm"
                                onClick={() => onViewOrder(order)}
                            >
                                {getTranslation("orderPage.viewFullOrder", language)}
                                <img
                                    className="w-4 h-4 ml-2 dark:invert dark:opacity-80"
                                    src={chevronRight || ""}
                                    alt={getTranslation("pagination.next", language)}
                                />
                            </button>
                        </div>
                    ) : (
                        <div className="flex flex-col space-y-3">
                            <div
                                className="w-full py-3 px-4 rounded-lg text-sm font-bold text-center"
                                style={{
                                    backgroundColor: getOptionStyles({ value: orderStatus }).backgroundColor,
                                    color: getOptionStyles({ value: orderStatus }).color
                                }}
                            >
                                {getTranslation(`orderStatuses.${orderStatus.toLowerCase()}`, language)}
                            </div>
                            <button
                                className="w-full py-3 px-4 rounded-lg text-sm font-bold flex justify-center items-center bg-customBlack dark:bg-[#21262d] text-white dark:text-[#c9d1d9] hover:bg-customHoverBlack dark:hover:bg-[#30363d] transition-colors shadow-sm"
                                onClick={() => onViewOrder(order)}
                            >
                                {getTranslation("orderPage.viewFullOrder", language)}
                                <img
                                    className="w-4 h-4 ml-2 dark:invert dark:opacity-80"
                                    src={chevronRight || ""}
                                    alt={getTranslation("pagination.next", language)}
                                />
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default OrderCard;

